import { useMemo } from "react";
import { NormalizedRelease } from "@/types/sofa";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, Legend } from "recharts";

const PLATFORM_COLORS: Record<string, string> = {
  macOS: "hsl(220, 70%, 55%)",
  iOS: "hsl(350, 70%, 55%)",
  iPadOS: "hsl(350, 70%, 65%)",
  Safari: "hsl(200, 70%, 55%)",
  watchOS: "hsl(30, 80%, 55%)",
  tvOS: "hsl(270, 60%, 55%)",
  visionOS: "hsl(160, 60%, 45%)",
  Other: "hsl(0, 0%, 60%)",
};

function normalizePlatform(productName: unknown): string {
  const lower = typeof productName === "string" ? productName.toLowerCase() : "";
  if (!lower) return "Other";
  if (lower.includes("macos")) return "macOS";
  if (lower.includes("ipados")) return "iPadOS";
  if (lower.includes("ios")) return "iOS";
  if (lower.includes("safari") || lower.includes("webkit")) return "Safari";
  if (lower.includes("watchos")) return "watchOS";
  if (lower.includes("tvos")) return "tvOS";
  if (lower.includes("visionos")) return "visionOS";
  return "Other";
}

interface PlatformBreakdownChartProps {
  releases: NormalizedRelease[];
}

export function PlatformBreakdownChart({ releases }: PlatformBreakdownChartProps) {
  const data = useMemo(() => {
    const map = new Map<string, { cves: Set<string>; exploited: Set<string> }>();

    for (const release of releases) {
      const platform = normalizePlatform(release.productName);
      if (!map.has(platform)) map.set(platform, { cves: new Set(), exploited: new Set() });
      const entry = map.get(platform)!;
      for (const cve of release.cves) {
        entry.cves.add(cve.id);
        if (cve.detail.actively_exploited) entry.exploited.add(cve.id);
      }
    }

    return Array.from(map.entries())
      .map(([platform, { cves, exploited }]) => ({
        platform,
        cves: cves.size,
        exploited: exploited.size,
        color: PLATFORM_COLORS[platform] || PLATFORM_COLORS.Other,
      }))
      .sort((a, b) => b.cves - a.cves);
  }, [releases]);

  if (data.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-8">No data available</p>;
  }

  return (
    <div className="h-[250px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 0, right: 16, bottom: 0, left: 0 }}>
          <XAxis type="number" tick={{ fontSize: 11 }} />
          <YAxis
            type="category"
            dataKey="platform"
            width={70}
            tick={{ fontSize: 12 }}
          />
          <Tooltip
            contentStyle={{
              borderRadius: "8px",
              border: "1px solid hsl(var(--border))",
              background: "hsl(var(--popover))",
              color: "hsl(var(--popover-foreground))",
              fontSize: "12px",
            }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const d = payload[0]?.payload;
              if (!d) return null;
              const pct = d.cves > 0 ? ((d.exploited / d.cves) * 100).toFixed(1) : "0.0";
              return (
                <div className="rounded-lg border border-border bg-popover p-2.5 text-xs text-popover-foreground shadow-md space-y-1">
                  <p className="font-semibold">{d.platform}</p>
                  <p>Total CVEs: <span className="font-medium">{d.cves}</span></p>
                  <p>Actively Exploited: <span className="font-medium text-destructive">{d.exploited}</span></p>
                  <p className="text-muted-foreground">{pct}% exploited</p>
                </div>
              );
            }}
          />
          <Bar dataKey="cves" name="Total CVEs" radius={[0, 4, 4, 0]} barSize={18} fillOpacity={0.35}>
            {data.map((entry) => (
              <Cell key={entry.platform} fill={entry.color} />
            ))}
          </Bar>
          <Bar dataKey="exploited" name="Actively Exploited" radius={[0, 4, 4, 0]} barSize={18}>
            {data.map((entry) => (
              <Cell key={entry.platform} fill={entry.color} />
            ))}
          </Bar>
          <Legend iconSize={10} wrapperStyle={{ fontSize: "11px" }} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
